import {
  useBulkCreateServiceTicketsMutation,
  useCreateServiceTicketTaskMutation,
  useUpsertServiceTicketMutation,
} from '@/store/api/services/operations/serviceTickets/serviceTickets';
import type { Case } from '@/types';
import type {
  BulkCreateServiceTicketRequest,
  BulkCreateServiceTicketResult,
  TaskType,
  UpsertServiceTicketRequest,
} from '@/types/serviceTicket';
import toast from 'react-hot-toast';

import { getAssigneePayload } from './assignee';
import type { ServiceTicketCreateSchemaType } from './schema';
import { resolveLocationTargets } from './targetResolution';

interface UseServiceTicketCreateSubmitParams {
  // The case currently selected via case_id, if any - carries the location for single mode.
  selectedCase?: Case;
  selectedTaskTypes: TaskType[];
  onSingleCreated: (ticketId: string) => void;
  onBulkCreated: (results: BulkCreateServiceTicketResult[]) => void;
}

interface SubmitErrorShape {
  message?: string;
  data?: { message?: string };
  meta?: { message?: string };
}

const extractErrorMessage = (error: SubmitErrorShape, fallback: string) =>
  error?.meta?.message || error?.data?.message || error?.message || fallback;

/**
 * Submit logic shared by every service ticket creation entry point (the full page and the
 * modal), so the single vs. bulk branching, error handling, and task-assignment follow-up only
 * exist in one place.
 */
export const useServiceTicketCreateSubmit = ({
  selectedCase,
  selectedTaskTypes,
  onSingleCreated,
  onBulkCreated,
}: UseServiceTicketCreateSubmitParams) => {
  const [upsertServiceTicket, { isLoading: isCreatingSingle }] = useUpsertServiceTicketMutation();
  const [createServiceTicketTask] = useCreateServiceTicketTaskMutation();
  const [bulkCreateServiceTickets, { isLoading: isCreatingBulk }] =
    useBulkCreateServiceTicketsMutation();

  const submitBulk = async (data: ServiceTicketCreateSchemaType) => {
    const { locationIds } = resolveLocationTargets(data.targets || []);

    const apiPayload: BulkCreateServiceTicketRequest = {
      location_ids: locationIds,
      start_date: data.schedule_date
        ? new Date(data.schedule_date).toISOString()
        : new Date().toISOString(),
      details: data.description || '',
      // Schema requires priority (min length 1), so this is always set - no client-side default here.
      priority: data.priority.toLowerCase(),
      ...getAssigneePayload(data),
    };
    // Boro's bulk endpoint (service-tickets/bulk) has no case_id field - a shared case is
    // linked per-ticket below via the same PATCH the single-ticket flow already uses.
    if (data.route) apiPayload.route_id = data.route;
    if (selectedTaskTypes.length) {
      apiPayload.task_type_ids = selectedTaskTypes.map((taskType) => taskType.id);
    }

    try {
      const response = await bulkCreateServiceTickets(apiPayload).unwrap();
      const results = response?.data?.results || [];
      onBulkCreated(results);

      const successCount = results.filter((result) => result.success).length;
      if (results.length === 0) {
        toast.error('No service tickets were created');
      } else if (successCount === results.length) {
        toast.success(`${successCount} service ticket(s) created successfully`);
      } else if (successCount === 0) {
        toast.error(`Failed to create ${results.length} service ticket(s)`);
      } else {
        toast.error(
          `${successCount} of ${results.length} service ticket(s) created — ${results.length - successCount} failed`,
        );
      }

      if (data.case_id) {
        const createdTicketIds = results
          .filter((result) => result.success && result.service_ticket_id)
          .map((result) => result.service_ticket_id as string);

        if (createdTicketIds.length > 0) {
          try {
            await Promise.all(
              createdTicketIds.map((ticketId) =>
                upsertServiceTicket({ id: ticketId, case_id: data.case_id }).unwrap(),
              ),
            );
          } catch (linkError) {
            console.error('Service Ticket Case Link Error:', linkError);
            toast.error(
              'Tickets were created, but linking them to the case failed. You can link them from each ticket.',
            );
          }
        }
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error as SubmitErrorShape, 'Failed to create service tickets'),
      );
    }
  };

  const submitSingle = async (data: ServiceTicketCreateSchemaType) => {
    if (!data.case_id && !data.location_id) {
      toast.error('Either a case or a location must be selected');
      return;
    }

    const locationId =
      data.case_id && selectedCase?.location_id
        ? selectedCase.location_id
        : data.location_id
          ? Number.parseInt(data.location_id, 10)
          : 0;

    const assignee = getAssigneePayload(data);
    const apiPayload: UpsertServiceTicketRequest = {
      location_id: locationId,
      start_date: data.schedule_date
        ? new Date(data.schedule_date).toISOString()
        : new Date().toISOString(),
      details: data.description || '',
      // Schema requires priority (min length 1), so this is always set - no client-side default here.
      priority: data.priority.toLowerCase(),
      // No assignee selected keeps today's technician_id: 0 (unassigned).
      ...(assignee.technician_id || assignee.ops_service_id ? assignee : { technician_id: 0 }),
    };

    if (data.case_id) apiPayload.case_id = data.case_id;
    if (data.route) apiPayload.route_id = data.route;

    try {
      const created = await upsertServiceTicket(apiPayload).unwrap();
      const newTicketId = created?.data?.id;

      // Tasks are created through a separate endpoint once the ticket exists. A task
      // failure shouldn't lose the created ticket - warn and let the user finish from
      // the ticket itself.
      if (newTicketId && selectedTaskTypes.length > 0) {
        try {
          await Promise.all(
            selectedTaskTypes.map((taskType) =>
              createServiceTicketTask({
                serviceTicketId: newTicketId,
                task_type_id: taskType.id,
                description: taskType.description || undefined,
              }).unwrap(),
            ),
          );
        } catch (taskError) {
          console.error('Service Ticket Task Create Error:', taskError);
          toast.error(
            'Service ticket created, but some tasks could not be added. You can add them from the ticket.',
          );
          onSingleCreated(newTicketId);
          return;
        }
      }

      toast.success('Service ticket created successfully');
      if (newTicketId) onSingleCreated(newTicketId);
    } catch (error) {
      console.error('Service Ticket Create Error:', error);
      toast.error(
        extractErrorMessage(error as SubmitErrorShape, 'Failed to create service ticket'),
      );
    }
  };

  const onSubmit = async (data: ServiceTicketCreateSchemaType) => {
    if (data.targetMode === 'id') {
      await submitBulk(data);
    } else {
      await submitSingle(data);
    }
  };

  return {
    onSubmit,
    isSubmitting: isCreatingSingle || isCreatingBulk,
  };
};
